import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckSquare, UserPlus, MoreHorizontal } from 'lucide-react';
import { useFamily } from '@/hooks/useFamily';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { usePersonProfiles, PersonProfile } from '@/hooks/usePersonProfiles';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { VisualPersonCard } from '@/components/persons/VisualPersonCard';
import { CategorySection } from '@/components/persons/CategorySection';
import { PersonsGrid } from '@/components/persons/PersonsGrid';
import { CreatePersonForm } from '@/components/persons/CreatePersonForm';
import { Plus, Search, Users, Heart, Stethoscope } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useModalNavigation } from "@/hooks/useModalNavigation";
import { ModernHero } from '@/components/ModernHero';
import personsHeroImage from '@/assets/persons-hero.jpg';
import { ConditionalWrapper } from '@/components/ConditionalWrapper';

export default function MobilePersons() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openModal } = useModalNavigation();
  const { profile, family, familyMembers } = useFamily();
  const { 
    persons, 
    loading: personsLoading, 
    createPerson, 
    updatePerson, 
    deletePerson,
    getPersonsByCategory,
    getEmergencyContacts,
    searchPersons,
    refreshPersons
  } = usePersonProfiles(family?.id);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [editingPerson, setEditingPerson] = useState<PersonProfile | null>(null);
  const [deletingPersonId, setDeletingPersonId] = useState<string | null>(null);

  const handleCall = (phone: string) => {
    window.location.href = `tel:${phone}`;
  };

  const handleEmail = (email: string) => {
    window.location.href = `mailto:${email}`;
  };

  const handleEditPerson = (person: PersonProfile) => {
    setEditingPerson(person);
  };

  const handleUpdatePerson = async (data: any) => {
    if (editingPerson) {
      await updatePerson(editingPerson.id, data);
      setEditingPerson(null);
    }
  };

  const handleDeletePerson = async () => {
    if (deletingPersonId) {
      await deletePerson(deletingPersonId);
      setDeletingPersonId(null);
    }
  };

  // Filter persons based on search and category
  let filteredPersons = persons;
  
  // Apply search filter first
  if (searchQuery) {
    filteredPersons = searchPersons(searchQuery);
  }
  
  // Then apply category filter on the search results
  if (selectedCategory !== 'all') {
    if (selectedCategory === 'emergency') {
      filteredPersons = filteredPersons.filter(person => person.emergency_contact);
    } else {
      filteredPersons = filteredPersons.filter(person => person.category === selectedCategory);
    }
  }

  // Check if user already has a person_profiles entry or a family entry with same name
  const hasSelfInPersons = persons.some(p => p.profile_id === profile?.id);
  const hasFamilyWithSameName = profile && persons.some(p => 
    p.category === 'family' && 
    p.first_name.toLowerCase().trim() === (profile.first_name || '').toLowerCase().trim() &&
    p.last_name.toLowerCase().trim() === (profile.last_name || '').toLowerCase().trim()
  );
  
  // Check if user has explicitly deleted their profile (stored in localStorage)
  const hasDeletedOwnProfile = localStorage.getItem(`deleted-profile-${profile?.id}`) === 'true';
  
  // Only create artificial profile if user doesn't have one and we have profile data and no family entry with same name and hasn't explicitly deleted it
  const currentUserProfile = (profile && !hasSelfInPersons && !hasFamilyWithSameName && !hasDeletedOwnProfile) ? {
    id: `profile-${profile.id}`, // Unique ID to distinguish from real person_profiles
    first_name: profile.first_name || 'Vous',
    last_name: profile.last_name || '',
    category: 'family' as const,
    family_role: 'parent' as const,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    family_id: family?.id,
    created_by: profile.id,
    profile_id: profile.id, // Link to actual profile for updates
    date_of_birth: profile.date_of_birth || null,
    phone_primary: '',
    phone_secondary: '',
    email: profile.email || '',
    address: '',
    notes: '',
    profession: '',
    emergency_contact: false,
    medical_speciality: '',
    relationship_type: '',
    gender: null,
    avatar_url: profile.avatar_url
  } : null;

  // Add current user to family category if we created the artificial profile with defensive deduplication
  if (currentUserProfile && (selectedCategory === 'all' || selectedCategory === 'family')) {
    // Check if current user profile matches search query if there is one
    const matchesSearch = !searchQuery || (
      currentUserProfile.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      currentUserProfile.last_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (currentUserProfile.profession && currentUserProfile.profession.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (currentUserProfile.notes && currentUserProfile.notes.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    
    if (matchesSearch) {
      if (selectedCategory === 'family') {
        const familyPersons = [currentUserProfile, ...getPersonsByCategory('family')];
        
        // Apply defensive deduplication
        const deduplicatedFamilyMap = new Map<string, any>();
        familyPersons.forEach(person => {
          const normalizedFirst = person.first_name.toLowerCase().trim();
          const normalizedLast = person.last_name.toLowerCase().trim();
          const key = person.profile_id ? `pid:${person.profile_id}` : `${person.family_id}:${person.category}:${normalizedFirst}|${normalizedLast}`;
          
          if (!deduplicatedFamilyMap.has(key)) {
            deduplicatedFamilyMap.set(key, person);
          }
        });
        
        filteredPersons = Array.from(deduplicatedFamilyMap.values());
        
        // Apply search filter to deduplicated results if there's a search query
        if (searchQuery) {
          filteredPersons = filteredPersons.filter(person =>
            person.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            person.last_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (person.notes && person.notes.toLowerCase().includes(searchQuery.toLowerCase()))
          );
        }
      } else if (selectedCategory === 'all' && searchQuery) {
        // If we're showing all categories and there's a search, add current user to filtered results if it matches
        filteredPersons = [currentUserProfile, ...filteredPersons];
      }
    }
  }

  // Count actual family members (including those with profile_id) + artificial profile if exists
  const familyCount = getPersonsByCategory('family').length + (currentUserProfile ? 1 : 0);
  
  const categoryCounts = {
    family: familyCount,
    extended_family: getPersonsByCategory('extended_family').length,
    friends: getPersonsByCategory('friends').length,
    services: getPersonsByCategory('services').length,
    medical_other: getPersonsByCategory('medical_other').length,
    emergency: getEmergencyContacts().length
  };

  const heroActions = [
    {
      icon: UserPlus,
      label: 'Nouvelle personne',
      onClick: () => openModal('createPerson')
    }
  ];

  return (
    <ConditionalWrapper
      type="page"
      skeletonType="profile"
      variant="persons"
      requiresFamily={true}
    >
      <>
        <ModernHero
          title="Famille"
          actions={heroActions}
          backgroundImage={personsHeroImage}
          category="persons"
          allowImageChange={true}
          height="40vh"
        />

        {/* Content Section */}
        <div className="relative -mt-24 bg-content-warm rounded-t-3xl pt-8 px-4 pb-8 min-h-fit z-20">
          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher une personne..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Categories with visual grids */}
          <div className="space-y-8">
            {/* Famille */}
            {(selectedCategory === 'all' || selectedCategory === 'family') && (
              <CategorySection
                title="Famille"
                icon="👨‍👩‍👧‍👦"
                persons={
                  searchQuery
                    ? filteredPersons.filter(p => p.category === 'family')
                    : (currentUserProfile
                        ? [currentUserProfile, ...getPersonsByCategory('family')]
                        : getPersonsByCategory('family'))
                }
              />
            )}

            {/* Famille étendue */}
            {(selectedCategory === 'all' || selectedCategory === 'extended_family') && (
              <CategorySection
                title="Famille étendue"
                icon="👨‍👩‍👧‍👦"
                persons={searchQuery ? filteredPersons.filter(p => p.category === 'extended_family') : getPersonsByCategory('extended_family')}
              />
            )}

            {/* Amis */}
            {(selectedCategory === 'all' || selectedCategory === 'friends') && (
              <CategorySection
                title="Amis"
                icon="👥"
                persons={searchQuery ? filteredPersons.filter(p => p.category === 'friends') : getPersonsByCategory('friends')}
              />
            )}

            {/* Services */}
            {(selectedCategory === 'all' || selectedCategory === 'services') && (
              <CategorySection
                title="Services"
                icon="🛠️"
                persons={searchQuery ? filteredPersons.filter(p => p.category === 'services') : getPersonsByCategory('services')}
              />
            )}

            {/* Médical/Autres */}
            {(selectedCategory === 'all' || selectedCategory === 'medical_other') && (
              <CategorySection
                title="Médical/Autres"
                icon="🩺"
                persons={searchQuery ? filteredPersons.filter(p => p.category === 'medical_other') : getPersonsByCategory('medical_other')}
              />
            )}

            {/* Urgences */}
            {(selectedCategory === 'all' || selectedCategory === 'emergency') && (
              <CategorySection
                title="Urgences"
                icon="🚨"
                persons={searchQuery ? filteredPersons.filter(p => p.emergency_contact) : getEmergencyContacts()}
              />
            )}

            {/* Empty state when no categories match */}
            {persons.length === 0 && (
              <div className="text-center py-12">
                <UserPlus className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">
                  Vous n'avez pas encore ajouté de personnes
                </p>
                <Button onClick={() => openModal('createPerson')} size="lg">
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter une personne
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Edit Person Form */}
        {editingPerson && (
          <CreatePersonForm
            isOpen={!!editingPerson}
            onClose={() => setEditingPerson(null)}
            onSubmit={handleUpdatePerson}
            initialData={editingPerson}
            title="Modifier la personne"
            onDelete={deletePerson}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deletingPersonId} onOpenChange={() => setDeletingPersonId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer cette personne</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible. La personne sera définitivement supprimée.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeletePerson}>
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    </ConditionalWrapper>
  );
}